import { FCC, Id, Loading } from "@dgs/core";
import { createContext, useContext, useEffect } from "react";
import { useRegionQuery } from "../shared/api/regions/useRegionsQuery";
import { useRegistrationToken } from "../shared/hooks/useRegistrationToken";
import { RegionShowResource } from "../shared/types/region";
import { useSessionState } from "../shared/utils/useSessionState";
import { RegionModal } from "./RegionModal";

interface RegionProviderState {
	region: RegionShowResource | undefined;
	regionId: Id | null;
}

const RegionContext = createContext<RegionProviderState | undefined>(undefined);

const showRegionModal = (region: RegionShowResource) => region.isFallback && !region.onlyOneRegionExists;

export const RegionProvider: FCC = ({ children }) => {
	const [regionId, setRegionId] = useSessionState<Id | null>("region", null);
	const registrationToken = useRegistrationToken();

	const { region } = useRegionQuery(regionId, registrationToken);

	useEffect(() => {
		if (region && !showRegionModal(region)) {
			setRegionId(region.id);
		}
	}, [region, setRegionId]);

	return (
		<RegionContext.Provider value={{ region, regionId }}>
			{!region ? (
				<Loading />
			) : (
				<>
					{showRegionModal(region) && <RegionModal setRegionId={setRegionId} />}
					{children}
				</>
			)}
		</RegionContext.Provider>
	);
};

export const useRegion = () => {
	const context = useContext(RegionContext);

	if (context === undefined) {
		throw new Error("Context undefined. Did you forget to wrap your components in the RegionProvider?");
	}

	return context;
};
